// game/occupant-seats.js: which Lua seat (1 to 4) an occupant_id holds.
//
// BOT_SUBSTITUTED and SEAT_RECLAIMED name only an occupant_id. The
// snapshot's seat_occupants has names but no occupant ids, so the seat
// comes from pairs the game itself sends:
//   • GAME_INITIALIZED: your_occupant_id + your_seat_index, and
//     dealer_occupant_id + the snapshot's dealer_seat
//   • any event with actor_occupant_id + actor_seat (bids, plays, discards)
//   • a turn timer or timeout (actor_occupant_id) + the current_turn_seat
//     of the snapshot just before it: the server starts a turn's timer
//     right after the event that moved the turn there
// The room's player list (occupant_id + seat_index) fills in seats the game
// has not named yet. The live list has no human seats in a fresh game, so
// the room hook works them out at GAME_STARTING (seatsAtGameStart below).
// Every seat learned is kept for the whole game, so a player who left the
// room list still resolves; a new game_id starts again. Display names are
// never used: two players can share one.

/** Events whose actor_occupant_id is the seat on turn. */
const TURN_EVENTS = new Set(['BID_TIMER_STARTED', 'PLAY_TIMER_STARTED', 'BID_TIMEOUT', 'PLAY_TIMEOUT']);

const TEAM_SEATS = { A: [0, 2], B: [1, 3] };

/**
 * Each human's seat (0 to 3) at game start, by the server's seating rule
 * (server/src/room/room.js _applyStartGame): humans with a team pick take
 * their team's seats in join order, then the rest take the lowest open
 * seat in join order, and bots fill what is left. The seats left for bots
 * must be exactly the bot seats GAME_STARTING names; if not, the rule
 * here and the server disagree, so nothing is returned and a warning says
 * so (the game then learns seats only from its own events).
 *
 * @param {Array<{ occupant_id: string, join_order?: number|null, team?: string|null }>} humans
 *   the room list just before GAME_STARTING, in the order the server sent it
 * @param {Array<{ seat_index: number }>|null|undefined} botAssignments
 * @returns {Map<string, number>}
 */
export function seatsAtGameStart(humans, botAssignments) {
  /** @type {Map<string, number>} */
  const out = new Map();
  if (!Array.isArray(botAssignments)) {
    console.warn('[occupant-seats] GAME_STARTING has no bot_assignments; human seats stay unknown until the game names them');
    return out;
  }
  // A stable sort keeps the list order for equal join_order, like the server.
  const byJoin = [...(humans || [])].sort((a, b) => (a.join_order ?? 0) - (b.join_order ?? 0));
  /** @type {(string|null)[]} */
  const seatTo = [null, null, null, null];
  for (const team of /** @type {const} */ (['A', 'B'])) {
    byJoin.filter((o) => o.team === team).forEach((o, i) => {
      const seat = TEAM_SEATS[team][i];
      if (seat != null) seatTo[seat] = o.occupant_id;
    });
  }
  for (const o of byJoin) {
    if (o.team === 'A' || o.team === 'B') continue;
    const seat = seatTo.indexOf(null);
    if (seat >= 0) seatTo[seat] = o.occupant_id;
  }
  const openSeats = seatTo.flatMap((id, seat) => (id === null ? [seat] : []));
  const botSeats = botAssignments.map((b) => b?.seat_index).sort((a, b) => a - b);
  if (openSeats.join(',') !== botSeats.join(',')) {
    console.warn(`[occupant-seats] worked-out seats leave [${openSeats}] for bots, but GAME_STARTING put bots at [${botSeats}]; human seats stay unknown until the game names them`);
    return out;
  }
  seatTo.forEach((id, seat) => { if (id !== null) out.set(id, seat); });
  return out;
}

/**
 * @returns {{
 *   learnFromRoom: (occupants: any[]) => void,
 *   learnFromGameEvent: (event: any) => void,
 *   seatOf: (occupantId: string|null|undefined) => number|null,
 * }}
 */
export function createOccupantSeatTracker() {
  /** @type {Map<string, number>} seats the game named */
  const fromGame = new Map();
  /** @type {Map<string, number>} seats from the room list */
  const fromRoom = new Map();
  /** @type {string|null} */
  let gameId = null;
  /** @type {number|null} current_turn_seat of the latest snapshot */
  let turnSeat = null;

  /** @param {any} id @param {any} lua @param {Map<string, number>} into */
  function learn(id, lua, into) {
    if (typeof id === 'string' && id && Number.isInteger(lua) && lua >= 1 && lua <= 4) into.set(id, lua);
  }

  return {
    learnFromRoom(occupants) {
      for (const o of occupants || []) {
        if (typeof o?.seat_index === 'number') learn(o.occupant_id, o.seat_index + 1, fromRoom);
      }
    },
    learnFromGameEvent(event) {
      const p = event?.payload;
      if (!p || typeof p !== 'object') return;
      if (event.type === 'GAME_INITIALIZED') {
        // A new game seats everyone again.
        if (p.game_id != null && p.game_id !== gameId) {
          gameId = p.game_id;
          fromGame.clear();
          // Room seats of an old game are wrong now; the next room list
          // (the view passes it on every render) fills them in again.
          fromRoom.clear();
        }
        if (typeof p.your_seat_index === 'number') learn(p.your_occupant_id, p.your_seat_index + 1, fromGame);
        learn(p.dealer_occupant_id, p.game_state_snapshot?.dealer_seat, fromGame);
      }
      learn(p.actor_occupant_id, p.actor_seat, fromGame);
      if (TURN_EVENTS.has(event.type) && p.actor_seat == null) learn(p.actor_occupant_id, turnSeat, fromGame);
      const snap = p.game_state_snapshot;
      if (snap && typeof snap === 'object') turnSeat = Number.isInteger(snap.current_turn_seat) ? snap.current_turn_seat : null;
    },
    seatOf(occupantId) {
      if (typeof occupantId !== 'string') return null;
      return fromGame.get(occupantId) ?? fromRoom.get(occupantId) ?? null;
    },
  };
}
